// Writes the commission engine's results for a saved deal back to the database:
// fee amounts, lease expiration, projected payments and auto reminders.

import { planDealReminders, reconcilePayments, reconcileReminders, type DealType } from './commission';
import { calculateDeal, num, toDealDates, type DealCalc } from './dealCalc';
import { todayISO } from './format';
import { supabase, unwrap } from './supabase';

// ---------------------------------------------------------------------------
// Sync: write the engine's results back to the database
// ---------------------------------------------------------------------------

export interface SyncResult {
  calc: DealCalc;
  payments: { inserted: number; updated: number; deleted: number; locked: number };
  reminders: { inserted: number; moved: number; closed: number };
}

/**
 * Recomputes a saved deal and brings its derived data in line: fee component amounts,
 * lease expiration, projected payments (invoiced/received are never touched) and
 * auto-generated reminders. Safe to run any number of times.
 */
export async function syncDeal(dealId: string): Promise<SyncResult> {
  const [deal, terms, abatements, fees, payout, payments, reminders] = await Promise.all([
    supabase.from('deals').select('*').eq('id', dealId).single(),
    supabase.from('deal_lease_terms').select('*').eq('deal_id', dealId).maybeSingle(),
    supabase.from('deal_rent_abatements').select('start_month, months, pct').eq('deal_id', dealId),
    supabase.from('fee_components').select('id, component_key, label, basis, rate, payer, split_pct, override_amount').eq('deal_id', dealId).order('sort_order'),
    supabase.from('payout_structures').select('*').eq('deal_id', dealId).maybeSingle(),
    supabase.from('payments').select('id, slot_key, status, amount, expected_date, received_date, received_amount, label').eq('deal_id', dealId),
    supabase.from('reminders').select('id, source_key, status, due_date').eq('deal_id', dealId).eq('auto_generated', true),
  ]);
  const d = unwrap(deal);
  const isLease = d.deal_type === 'tenant_rep' || d.deal_type === 'll_rep';
  const dates = toDealDates(d);
  const feeRows = unwrap(fees);
  const calc = calculateDeal({
    isLease,
    dates,
    leaseTerms: terms.data,
    abatements: unwrap(abatements),
    fees: feeRows,
    payout: payout.data,
    purchasePrice: num(d.purchase_price),
  });

  // Fee component amounts, for display and reporting.
  if (calc.fees) {
    for (const [i, r] of feeRows.entries()) {
      const calculated = calc.fees.components[i]?.calculated ?? null;
      unwrap(await supabase.from('fee_components').update({ calculated_amount: calculated }).eq('id', r.id));
    }
  }

  // Lease expiration follows commencement + term.
  if (isLease && (d.lease_expiration_date !== calc.expiration.date || d.lease_expiration_basis !== calc.expiration.basis)) {
    unwrap(await supabase.from('deals').update({ lease_expiration_date: calc.expiration.date, lease_expiration_basis: calc.expiration.basis }).eq('id', dealId));
  }

  // Payments: only when the plan is complete. An input error leaves existing rows alone.
  const stored = unwrap(payments);
  const result: SyncResult = { calc, payments: { inserted: 0, updated: 0, deleted: 0, locked: 0 }, reminders: { inserted: 0, moved: 0, closed: 0 } };
  if (calc.plan) {
    const changes = reconcilePayments(
      stored.map((p) => ({ id: p.id, slotKey: p.slot_key, status: p.status as 'projected', amount: Number(p.amount), expectedDate: p.expected_date })),
      calc.plan.payments,
    );
    for (const id of changes.toDelete) unwrap(await supabase.from('payments').delete().eq('id', id));
    for (const u of changes.toUpdate) {
      unwrap(
        await supabase
          .from('payments')
          .update({ label: u.changes.label, amount: u.changes.amount, trigger_event: u.changes.trigger, trigger_offset_days: u.changes.offsetDays, expected_date: u.changes.expectedDate })
          .eq('id', u.id),
      );
    }
    if (changes.toInsert.length) {
      unwrap(
        await supabase.from('payments').insert(
          changes.toInsert.map((p) => ({
            deal_id: dealId,
            slot_key: p.slotKey,
            label: p.label,
            amount: p.amount,
            trigger_event: p.trigger,
            trigger_offset_days: p.offsetDays,
            expected_date: p.expectedDate,
            status: 'projected',
          })),
        ),
      );
    }
    result.payments = { inserted: changes.toInsert.length, updated: changes.toUpdate.length, deleted: changes.toDelete.length, locked: changes.untouched.length };
  }

  // Auto reminders, from the payments as they now stand.
  const current = unwrap(await supabase.from('payments').select('id, slot_key, status, expected_date, received_date, label').eq('deal_id', dealId));
  const planned = planDealReminders({
    dealId,
    dealType: d.deal_type as DealType,
    dealName: d.name,
    dates,
    termMonths: num(terms.data?.term_months),
    payments: current.map((p) => ({ id: p.id, slotKey: p.slot_key, label: p.label ?? undefined, status: p.status as 'projected', expectedDate: p.expected_date, receivedDate: p.received_date })),
    today: todayISO(),
  });
  const existing = unwrap(reminders).map((r) => ({ id: r.id, sourceKey: r.source_key!, status: r.status as 'open', dueDate: r.due_date }));
  const rc = reconcileReminders(existing, planned);
  for (const u of rc.toUpdate) unwrap(await supabase.from('reminders').update({ due_date: u.dueDate, note: u.note }).eq('id', u.id));
  for (const id of rc.toClose) unwrap(await supabase.from('reminders').update({ status: 'done', completed_at: new Date().toISOString() }).eq('id', id));
  if (rc.toInsert.length) {
    unwrap(
      await supabase.from('reminders').insert(
        rc.toInsert.map((r) => {
          const slot = r.sourceKey.match(/payment_chase:(.+)$/)?.[1];
          return {
            deal_id: dealId,
            contact_id: d.primary_contact_id,
            payment_id: slot ? (current.find((p) => (p.slot_key ?? p.id) === slot)?.id ?? null) : null,
            due_date: r.dueDate,
            note: r.note,
            reminder_type: r.type,
            auto_generated: true,
            source_key: r.sourceKey,
          };
        }),
      ),
    );
  }
  result.reminders = { inserted: rc.toInsert.length, moved: rc.toUpdate.length, closed: rc.toClose.length };
  return result;
}
